import documentHistoryGuard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/documentHistoryGuard';
import { setPendingDiscard } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import * as React from 'react';

/** The internal link the click targets, `null` for anything the guard must let through. */
const findInternalLink = (event: MouseEvent): URL | null => {
	if (
		event.defaultPrevented ||
		event.button !== 0 ||
		event.metaKey ||
		event.ctrlKey ||
		event.shiftKey ||
		event.altKey
	) {
		return null;
	}

	if (!(event.target instanceof Element)) {
		return null;
	}

	const anchor = event.target.closest('a[href]');

	if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute('download')) {
		return null;
	}

	if (anchor.target !== '' && anchor.target !== '_self') {
		return null;
	}

	const url = new URL(anchor.href, window.location.href);

	if (url.origin !== window.location.origin) {
		return null;
	}

	if (url.pathname === window.location.pathname && url.search === window.location.search) {
		return null;
	}

	return url;
};

/**
 * Guards the ways of leaving the screen that the declarative router cannot block while a draft is
 * modified (`isActive`): a click on any internal link, sidebar included, and the browser's back
 * button. Each queues a discard request instead; the page-unload case stays with `useBeforeUnloadGuard`.
 * While active, one marked history entry sits above the screen's own (`documentHistoryGuard`).
 */
const useNavigationGuard = (isActive: boolean): void => {
	const isActiveRef = React.useRef(isActive);
	isActiveRef.current = isActive;

	React.useEffect(() => {
		const handleClick = (event: MouseEvent): void => {
			if (!isActiveRef.current) {
				return;
			}

			const url = findInternalLink(event);

			if (url === null) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			setPendingDiscard({ type: 'back', path: `${url.pathname}${url.search}${url.hash}` });
		};

		const handlePopState = (): void => {
			if (documentHistoryGuard.isPopStateExpected()) {
				documentHistoryGuard.consumePopState();
				return;
			}

			if (!isActiveRef.current) {
				return;
			}

			documentHistoryGuard.pushSentinel();
			setPendingDiscard({ type: 'history-back' });
		};

		document.addEventListener('click', handleClick, true);
		window.addEventListener('popstate', handlePopState);

		return () => {
			document.removeEventListener('click', handleClick, true);
			window.removeEventListener('popstate', handlePopState);
		};
	}, []);

	// No cleanup on purpose: a cleanup would also run on a strict-mode re-mount and on unmount, where
	// stepping back would leave the screen instead of the sentinel.
	React.useEffect(() => {
		if (isActive) {
			documentHistoryGuard.pushSentinel();
		} else {
			documentHistoryGuard.removeSentinel();
		}
	}, [isActive]);
};

export default useNavigationGuard;
